import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { isWidthUp } from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import reactHtmlParser from 'react-html-parser';

import { INPUT_PARAM_DEFAULTS } from '../constants';
import { useStylesChildren } from '../styles';
import Card from '../../Card';
import Accordion from '../../Accordion';
import Chips from '../../Chips';
import TextField from '../../TextField';
import { generateInputParamsAttributesPdf } from '../../InputParameters/constants';

const Input = ({ screenWidth, ...props }) => {
	const { t } = useTranslation();
	const classes = useStylesChildren();
	const { values, touched, errors, handleChange, handleBlur, setValues } = props.formikProps;

	const changeLineFrequency = lineFrequency => {
		setValues({ ...values, lineFrequency });
	};

	const changeSupplyType = type => {
		if (type === 'DC') {
			setValues({
				vinMin: INPUT_PARAM_DEFAULTS.VIN_MIN.DC,
				vinMax: INPUT_PARAM_DEFAULTS.VIN_MAX.DC,
				vinNom: INPUT_PARAM_DEFAULTS.VIN_NOM,
				vout: INPUT_PARAM_DEFAULTS.VOUT.DC,
				iout: INPUT_PARAM_DEFAULTS.IOUT,
				ambientTemperature: INPUT_PARAM_DEFAULTS.AMB_TEMP,
				supplyType: 'DC',
				lineFrequency: INPUT_PARAM_DEFAULTS.LINE_FREQUENCY,
				isolated: INPUT_PARAM_DEFAULTS.ISOLATED,
				automotiveDesign: INPUT_PARAM_DEFAULTS.AUTOMOTIVE_DESIGN
			});
		} else {
			setValues({
				vinMin: INPUT_PARAM_DEFAULTS.VIN_MIN.AC,
				vinMax: INPUT_PARAM_DEFAULTS.VIN_MAX.AC,
				vinNom: INPUT_PARAM_DEFAULTS.VIN_NOM,
				vout: INPUT_PARAM_DEFAULTS.VOUT.AC,
				iout: INPUT_PARAM_DEFAULTS.IOUT,
				ambientTemperature: INPUT_PARAM_DEFAULTS.AMB_TEMP,
				supplyType: 'AC',
				lineFrequency: INPUT_PARAM_DEFAULTS.LINE_FREQUENCY,
				isolated: INPUT_PARAM_DEFAULTS.ISOLATED,
				automotiveDesign: INPUT_PARAM_DEFAULTS.AUTOMOTIVE_DESIGN
			});
		}
	};

	return (
		<Card
			title={t('components.inputs.input.title')}
			actions={
				<Accordion
					title={t('components.inputs.input.accordion.title')}
					content={
						<Grid container className={classes.accordionContent}>
							<Grid item xs>
								<TextField
									id="Input__vinNom_text_field"
									label={reactHtmlParser(
										generateInputParamsAttributesPdf(values.supplyType).find(item => item.value === 'vinNom')
											.formattedLabel
									)}
									name="vinNom"
									InputProps={{
										endAdornment: <InputAdornment position="end">V</InputAdornment>
									}}
									maxLength={8}
									onChange={handleChange}
									onBlur={handleBlur}
									value={values.vinNom}
									helperText={touched.vinNom && errors.vinNom ? errors.vinNom : '\u00a0'}
									error={touched.vinNom && errors.vinNom}
								/>
							</Grid>
						</Grid>
					}
				/>
			}
			fullHeight>
			<Grid container direction="column" spacing={2}>
				<Grid item container spacing={3}>
					<Grid item xs={isWidthUp('md', screenWidth) ? 6 : false}>
						<Chips
							label={t('components.inputs.input.chips.supplyType')}
							value={values.supplyType}
							updateValue={changeSupplyType}
							chips={[
								{ label: t('components.inputs.input.chips.labels.dc'), value: 'DC' },
								{ label: t('components.inputs.input.chips.labels.ac'), value: 'AC' }
							]}
							className={classes.focusableChips}
							tabIndex={0}
						/>
					</Grid>

					{values.supplyType === 'AC' && (
						<Grid item xs={isWidthUp('md', screenWidth) ? 6 : false}>
							<Chips
								label={t('components.inputs.input.chips.frequency')}
								value={values.lineFrequency.toString()}
								updateValue={changeLineFrequency}
								chips={[
									{ label: t('components.inputs.input.chips.labels.50Hz'), value: '50' },
									{ label: t('components.inputs.input.chips.labels.60Hz'), value: '60' }
								]}
							/>
						</Grid>
					)}
				</Grid>

				<Grid item container spacing={3}>
					<Grid item xs={isWidthUp('md', screenWidth) ? 6 : 12}>
						<TextField
							id="Input__vinMin_text_field"
							autoFocus
							label={reactHtmlParser(
								generateInputParamsAttributesPdf(values.supplyType).find(item => item.value === 'vinMin').formattedLabel
							)}
							name="vinMin"
							InputProps={{
								endAdornment: <InputAdornment position="end">V</InputAdornment>
							}}
							maxLength={8}
							onChange={handleChange}
							onBlur={handleBlur}
							value={values.vinMin}
							helperText={touched.vinMin && errors.vinMin ? errors.vinMin : '\u00a0'}
							error={touched.vinMin && errors.vinMin}
							className={classes.focusableTextField}
						/>
					</Grid>

					<Grid item xs={isWidthUp('md', screenWidth) ? 6 : 12}>
						<TextField
							id="Input__vinMax_text_field"
							label={reactHtmlParser(
								generateInputParamsAttributesPdf(values.supplyType).find(item => item.value === 'vinMax').formattedLabel
							)}
							name="vinMax"
							InputProps={{
								endAdornment: <InputAdornment position="end">V</InputAdornment>
							}}
							maxLength={8}
							onChange={handleChange}
							onBlur={handleBlur}
							value={values.vinMax}
							helperText={touched.vinMax && errors.vinMax ? errors.vinMax : '\u00a0'}
							error={touched.vinMax && errors.vinMax}
							className={classes.focusableTextField}
							// tabindex={values.supplyType === 'DC' ? '1' : '0'}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Card>
	);
};

Input.propTypes = {
	screenWidth: PropTypes.number.isRequired
};

export default Input;
